/**
 * Stat Card Builder - Shared Utilities
 * 
 * Handles fetching and calculating individual stat card values using
 * the asset aggregation endpoint. Each stat card gets its own API call
 * based on its configuration.
 */

import type { StatCardConfig } from "../types";
import { isDateField } from "../utils";

/**
 * Converts a period string to a date range
 * 
 * @param period - Period type (e.g., "last-month", "current-year")
 * @returns Object with start and end date strings in ISO format (YYYY-MM-DD)
 */
export const periodToDateRange = (
  period: string
): { start: string; end: string } => {
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth();

  let startDate: Date;
  let endDate: Date = new Date(today); // End is always today

  switch (period) {
    case "current-month":
      startDate = new Date(currentYear, currentMonth, 1);
      endDate = new Date(currentYear, currentMonth + 1, 0);
      break;

    case "last-month":
      startDate = new Date(currentYear, currentMonth - 1, 1);
      endDate = new Date(currentYear, currentMonth, 0);
      break;

    case "last-30-days":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 30);
      break;

    case "current-year":
      startDate = new Date(currentYear, 0, 1);
      endDate = new Date(currentYear, 11, 31);
      break;

    case "last-year":
      startDate = new Date(currentYear - 1, 0, 1);
      endDate = new Date(currentYear - 1, 11, 31);
      break;

    case "last-365-days":
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 365);
      break;

    case "next-30-days":
      startDate = new Date(today);
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 30);
      break;

    case "next-month":
      startDate = new Date(currentYear, currentMonth + 1, 1);
      endDate = new Date(currentYear, currentMonth + 2, 0);
      break;

    case "next-365-days":
      startDate = new Date(today);
      endDate = new Date(today);
      endDate.setDate(today.getDate() + 365);
      break;

    case "next-year":
      startDate = new Date(currentYear + 1, 0, 1);
      endDate = new Date(currentYear + 1, 11, 31);
      break;

    default:
      // Default to all time
      startDate = new Date(0);
      endDate = new Date(today);
  }

  return {
    start: startDate.toISOString().split("T")[0],
    end: endDate.toISOString().split("T")[0],
  };
};

/**
 * Filters data by period (current/last month/year, or last 30/365 days)
 * 
 * @param data - Raw data with date fields
 * @param period - Period type
 * @returns Filtered data for the specified period
 */
const filterDataByPeriod = (
  data: Record<string, any>[],
  period: string,
  explicitColumn?: string
): Record<string, any>[] => {
  // Use explicit column if provided, otherwise auto-detect from data
  const dateField = explicitColumn
    ? (data.length > 0 && explicitColumn in data[0] ? explicitColumn : undefined)
    : (data.length > 0
      ? Object.keys(data[0]).find((key) => isDateField(key, data[0][key]))
      : undefined);

  if (!dateField) {
    return data;
  }

  const { start: startStr, end: endStr } = periodToDateRange(period);

  return data.filter((item) => {
    const itemDate = String(item[dateField] || "");
    return itemDate >= startStr && itemDate <= endStr;
  });
};

/**
 * Calculates a stat card value from raw aggregated data
 * 
 * @param data - Raw aggregated data from API
 * @param config - Stat card configuration
 * @returns Calculated numeric value for the stat card
 */
/**
 * Apply all stat-card-level column filters to a dataset.
 * Each filter condition is AND-combined; values within a condition are OR-combined.
 * The singular `filter` field and every entry in `filters` are all applied.
 */
export function applyStatCardFilters(
  data: Record<string, any>[],
  config: Pick<StatCardConfig, "filter" | "filters">
): Record<string, any>[] {
  // Normalise: singular filter + filters array → single list
  const conditions = [
    ...(config.filters ?? []),
    ...(config.filter ? [config.filter] : []),
  ];

  let filtered = data;
  for (const condition of conditions) {
    if (condition.values.length === 0) continue;
    filtered = filtered.filter((row) => {
      const rowValue = row[condition.column];
      return condition.values.some((filterValue: string) => {
        if (filterValue === "null") {
          return rowValue === null || rowValue === undefined;
        }
        return String(rowValue) === String(filterValue);
      });
    });
    if (filtered.length === 0) return [];
  }
  return filtered;
}

export const calculateStatValue = (
  data: Record<string, any>[],
  config: StatCardConfig
): number => {
  if (data.length === 0) {
    return 0;
  }

  // Apply stat-card-level filters (singular filter + filters array, AND-combined)
  let filteredData = applyStatCardFilters(data, config);
  if (filteredData.length === 0 && (config.filter || config.filters?.length)) {
    return 0;
  }

  // Apply period filtering if specified
  if (config.period) {
    filteredData = filterDataByPeriod(filteredData, config.period, config.periodColumn);
    if (filteredData.length === 0) {
      return 0;
    }
  }

  switch (config.aggregation) {
    case "sum": {
      // Sum aggregation: sum the field specified in dimensions[0] across all rows
      const field = config.dimensions[0];
      const total = filteredData.reduce((sum, item) => {
        const value = Number(item[field] ?? 0);
        return sum + (isNaN(value) ? 0 : value);
      }, 0);
      return total;
    }

    case "count": {
      // Count aggregation: count unique values in the dimension
      const dimension = config.dimensions[0];
      const uniqueValues = new Set(
        filteredData.map((item) => item[dimension]).filter(Boolean)
      );
      return uniqueValues.size;
    }

    case "average": {
      // Average aggregation: calculate mean of values in dimensions[0]
      // dimensions[0] = the value field to average (e.g., "cost" or "count")
      // dimensions[1] = optional grouping dimension or weight field
      //
      // If dimensions[1] is numeric, calculates weighted average: sum(values) / sum(weights)
      // If dimensions[1] is a string, groups by that dimension, sums per group, then averages
      
      const valueField = config.dimensions[0];
      const dimensionField = config.dimensions[1];

      if (dimensionField) {
        // Check if this is a grouping dimension (by seeing if it contains strings)
        // or if it's meant to be a numeric weight field
        const sampleValue = filteredData.length > 0 ? filteredData[0][dimensionField] : null;
        const isGroupingDimension = typeof sampleValue === "string" || sampleValue === null;

        if (isGroupingDimension) {
          // Group by the dimension, sum values per group, then average the group totals
          const groupedTotals = new Map<string, number>();
          
          filteredData.forEach((item) => {
            const groupKey = String(item[dimensionField] || "");
            const value = Number(item[valueField] ?? 0);
            const current = groupedTotals.get(groupKey) || 0;
            groupedTotals.set(groupKey, current + value);
          });

          const totals = Array.from(groupedTotals.values());
          if (totals.length === 0) return 0;
          
          const sum = totals.reduce((acc, val) => acc + val, 0);
          return Math.round(sum / totals.length);
        } else {
          // Numeric weight field: weighted average
          // sum(values) / sum(weights)
          const totalValue = filteredData.reduce((sum, item) => {
            const value = Number(item[valueField] ?? 0);
            return sum + (isNaN(value) ? 0 : value);
          }, 0);

          const totalWeight = filteredData.reduce((sum, item) => {
            const weight = Number(item[dimensionField] ?? 0);
            return sum + (isNaN(weight) ? 0 : weight);
          }, 0);

          return totalWeight > 0 ? Math.round(totalValue / totalWeight) : 0;
        }
      }

      // Simple average: sum(values) / count(values)
      const sum = filteredData.reduce((total, item) => {
        const value = Number(item[valueField] ?? 0);
        return total + (isNaN(value) ? 0 : value);
      }, 0);
      
      const count = filteredData.filter(
        (item) => item[valueField] != null && !isNaN(Number(item[valueField]))
      ).length;
      
      return count > 0 ? Math.round(sum / count) : 0;
    }

    case "custom": {
      // Custom aggregation: use provided function
      if (config.customFn) {
        return config.customFn(filteredData);
      }
      return 0;
    }

    default:
      return 0;
  }
};

/**
 * Validates that a stat card configuration has the correct number of dimensions
 * for its aggregation type
 * 
 * @param config - Stat card configuration to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateStatCardConfig = (config: StatCardConfig): string | undefined => {
  const dimCount = config.dimensions.length;

  switch (config.aggregation) {
    case "sum":
    case "count":
      if (dimCount !== 1) {
        return `${config.aggregation} aggregation requires exactly 1 dimension, got ${dimCount}`;
      }
      break;

    case "average":
      if (dimCount !== 2) {
        return `average aggregation requires exactly 2 dimensions, got ${dimCount}`;
      }
      break;

    case "custom":
      if (!config.customFn) {
        return `custom aggregation requires a customFn function`;
      }
      break;
  }

  return undefined;
};
